import { Component, AfterViewInit, OnInit, OnDestroy, signal } from '@angular/core';

import { Subject, Subscription, takeUntil } from 'rxjs';

import { SondermindApplicationService } from '@sondermind/data-access/sondermind-app-service';
import {
  IconColors,
  IconNames,
  IconSizes,
  IconUsageTypes
} from '@sondermind/iris/icon';
import { ProviderUser, ProfileTabs } from '@sondermind/utilities/models-user-provider';
import { ITabItem } from '@sondermind/iris/tab';

@Component({
  selector: 'member-profile-app',
  templateUrl: './profile-app.component.html',
  styleUrls: ['./profile-app.component.scss'],
  standalone: false
})
export class ProfileAppComponent implements OnInit, OnDestroy, AfterViewInit {
  private destroyed$ = new Subject<void>();

  profileTabs = ProfileTabs;
  iconUsageTypes = IconUsageTypes;
  iconSizes = IconSizes;
  iconColors = IconColors;
  user: ProviderUser = null;
  userSub: Subscription;
  errorTabs = {};
  tabItems: ITabItem[] = [];

  readonly isSaasEnabledProvider = signal<boolean>(false);

  constructor(
    private appService: SondermindApplicationService
  ) {}

  ngOnInit(): void {
    this.appService.currentUser$.pipe(takeUntil(this.destroyed$))
      .subscribe((u) => {
        this.user = u;
        this.isSaasEnabledProvider.set(u.isSaasEnabled());
        this.setErrorTabs();

        this.setTabs(u.psychiatrist, u.isSaasEnabled());
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  ngAfterViewInit(): void {
    this.appService.pageLoaded = true;
  }

  setTabs(isPsychProvider: boolean, isSaasEnabled: boolean): void {
    if (!isSaasEnabled) {
      let tabs = [
        { title: ProfileTabs.ABOUT_YOU, route: '/profile', dataTest: 'tab-about-you' },
        { title: ProfileTabs.CREDENTIALS, route: '/profile/credentials', dataTest: 'tab-credentials' },
        { title: ProfileTabs.YOUR_BIO, route: '/profile/bio', dataTest: 'tab-bio' },
        { title: ProfileTabs.CLIENT_FOCUS, route: '/profile/focus', dataTest: 'tab-client-focus' },
        { title: ProfileTabs.SPECIALTIES, route: '/profile/specialties', dataTest: 'tab-specialties' },
        { title: ProfileTabs.APPROACHES, route: '/profile/approaches', dataTest: 'tab-approaches' },
        { title: ProfileTabs.IDENTITY, route: '/profile/identity', dataTest: 'tab-identity' },
      ];

      // If we are a psych provider, then hide the approach tab
      if (isPsychProvider) {
        tabs = tabs.filter((x) => x.title !== ProfileTabs.APPROACHES);
      }

      this.tabItems = tabs;

      // Set the icon and icon color for each tab item based on the errorTabs object
      this.tabItems = this.tabItems.map((tab) => ({
        ...tab,
        icon: this.errorTabs[tab.title] ? IconNames.WARNING : IconNames.CHECK,
        iconColor: this.errorTabs[tab.title] ? this.iconColors.DANGER : this.iconColors.SUCCESS
      }));
    } else {
      // SAAS providers do not need to have any other profile data besides the ABOUT-YOU section
      this.tabItems = [];
    }
  }

  private setErrorTabs(): void {
    this.errorTabs = {}; // Reset in case of user reload
    if (this.user?.profileErrorTabs?.length > 0) {
      Object.values(this.profileTabs).forEach((tabName) => {
        this.errorTabs[tabName] = this.hasError(tabName);
      });
    }
  }

  private hasError(tabName: string): boolean {
    return this.user && this.user.profileErrorTabs.findIndex((t) => t.name === tabName) >= 0;
  }
}
